// Round THEME-2 (2026-10-03) — theme-aware colours for components that keep a colour as DATA
// (a status map, a department colour, a tone table) and paint it inline.
//
// The components keep their original light colours. These helpers wrap a colour in the theme
// tokens from assets/styles/appThemeSync.css, with the original colour as the fallback:
//   inkFor("#405189")  -> "var(--ssw-ink-blue, #405189)"   coloured TEXT / icon
//   tintFor("#fde8e4") -> "var(--ssw-tint-red, #fde8e4)"   soft coloured BACKGROUND
// Light · Classic defines no ink/tint tokens, so it renders exactly the original colour; the dark
// themes swap in a readable ink / a soft tint of the same hue.
//
// Keep the raw colour wherever code derives another colour from it (`${hex}1A`, hexToRgba(hex)),
// because var() cannot be combined that way.

const parseHex = (value) => {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value || "").trim());
    if (!m) return null;
    let h = m[1];
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

export const hueName = (value) => {
    const rgb = parseHex(value);
    if (!rgb) return null;
    const [r, g, b] = rgb.map((v) => v / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
    if (d < 0.05) return "gray";
    let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
    if (h < 15 || h >= 345) return "red";
    if (h < 40) return "orange";
    if (h < 65) return "yellow";
    if (h < 160) return "green";
    if (h < 185) return "teal";
    if (h < 205) return "cyan";
    if (h < 232) return "blue";
    if (h < 255) return "indigo";
    if (h < 290) return "purple";
    return "pink";
};

/** Coloured text / icon that stays readable in every theme. */
export const inkFor = (value) => {
    const hue = hueName(value);
    return hue ? `var(--ssw-ink-${hue}, ${value})` : value;
};

/** Soft coloured background (chip, icon disc, highlighted cell) that follows the theme. */
export const tintFor = (value) => {
    const hue = hueName(value);
    return hue ? `var(--ssw-tint-${hue}, ${value})` : value;
};
